import { createContext } from 'react';
import { getPlatformStyles } from '@direct-frontend/platform-code';
import { Collection, Node } from '@react-types/shared';

import { cn } from '../../lib/css/className/className';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';

import { BaseItem, Option, Section } from './Item/types';
import { isSection } from './Item/utils';
import { ItemMobileProps, ListSize } from './types';

import './Icon/Icon.css';

getPlatformStyles({
    common: import('./ListBoxBase.css'),
    mobile: import('./ListBoxBase@mobile.css'),
    desktop: import('./ListBoxBase@desktop.css'),
});

export const cnListBox = cn('ListBox');
export const cnListBoxSection = cn('ListBoxSection');
export const cnListBoxSeparator = cn('ListBoxSeparator');
export const cnListBoxItem = cn('ListBoxItem');
export const cnListBoxItemSeparator = cn('ListBoxItemSeparator');

export const ListContext = createContext<{ size: ListSize; displaySelection?: boolean }>({
    size: 'xs',
    displaySelection: false,
});

interface useItemClassesProps extends ItemMobileProps {
    isFocused: boolean;
    isDisabled: boolean;
    isHovered?: boolean;
    isPressed?: boolean;
    hasAddonRight?: boolean;
}

export function useItemClasses(props: useItemClassesProps) {
    const { isFocused, isDisabled, isHovered, isPressed, hasAddonRight, isFirst, isLast } = props;

    const colorSkinClass = getColorSkin('gray-neutral', {
        isDisabled,
        isHovered: (!isDisabled && isHovered) || isPressed || isFocused,
    });

    const className = cnListBoxItem(
        {
            'has-addon-right': hasAddonRight,
            first: isFirst,
            last: isLast,
        },
        [colorSkinClass],
    );

    return {
        className,
    };
}

export function getDisabledKeys<
    ValueType extends string,
    SectionType extends string,
    TSingleItem extends BaseItem,
    TSection extends Section<SectionType, TSingleItem>,
>(items: (TSingleItem | TSection)[], getItemKey: (i: TSingleItem) => ValueType): ValueType[] {
    return getKeys<ValueType, SectionType, TSingleItem, TSection>(items, getItemKey, item => Boolean(item.disabled));
}

export function getKeys<
    ValueType extends string,
    SectionType extends string,
    TSingleItem extends BaseItem,
    TSection extends Section<SectionType, TSingleItem>,
>(
    items: (TSingleItem | TSection)[],
    getItemKey: (i: TSingleItem) => ValueType,
    predicate: (item: TSingleItem) => boolean = () => true,
): ValueType[] {
    return items.reduce((result: ValueType[], item) => {
        if (isSection<SectionType, TSingleItem>(item)) {
            result.push(...item.options.filter(predicate).map(getItemKey));

            return result;
        }

        if (predicate(item)) {
            result.push(getItemKey(item));
        }

        return result;
    }, []);
}

export function containsIconLeft<
    ValueType extends string,
    SectionType extends string,
    TSingleItem extends BaseItem,
    TSection extends BaseItem & Section<SectionType, TSingleItem>,
>(items: (TSingleItem | TSection)[]): boolean {
    return items.some(item => {
        if (isSection<ValueType, TSingleItem>(item)) {
            return item.options.some(option => Boolean(option.iconLeft));
        }

        return Boolean(item.iconLeft);
    });
}

/**
 * Функция, которая проверяет, что элемент пограничный, т.е. первый/последний в коллекции,
 * первый/последний в секции или первый после секции/последний перед секцией
 * @param item текущий элемент
 * @param nodes коллекция элементов
 * @param getItemKey функция, возвращающая уникальный ключ элемента
 * @returns Объект с полями isFirst: boolean, isLast: boolean
 */
export function isBorderElement<
    ValueType extends string,
    SectionType extends string,
    TSingleItem extends BaseItem,
    TSection extends Section<SectionType, TSingleItem>,
>(
    item: Option<ValueType>,
    nodes: Collection<Node<TSingleItem | TSection>>,
    getItemKey: (i: TSingleItem | TSection) => ValueType | SectionType,
): ItemMobileProps {
    // Получаем текущую, предыдущую и следующую ноды из коллекции
    const node = nodes.getItem(item.value);
    const prevKey = nodes.getKeyBefore(item.value);
    const nextKey = nodes.getKeyAfter(item.value);
    const prevItem = prevKey ? nodes.getItem(prevKey) : null;
    const nextItem = nextKey ? nodes.getItem(nextKey) : null;

    // Проверяем, что элемент первый или последний в секции
    const parentItem = node?.parentKey ? nodes.getItem(node.parentKey)?.value : null;
    const firstSectionItem = parentItem && isSection(parentItem) && parentItem.options[0];
    const isFirstInSection = firstSectionItem ? getItemKey(firstSectionItem) === item.value : false;
    const lastSectionItem = parentItem && isSection(parentItem) && parentItem.options[parentItem.options.length - 1];
    const isLastInSection = lastSectionItem ? getItemKey(lastSectionItem) === item.value : false;

    // Проверяем, что элемент не в секции, но сразу после или перед секцией
    const isFirstAfterSection = Boolean(!node?.parentKey && prevItem?.parentKey);
    const isLastBeforeSection = Boolean(!node?.parentKey && nextItem?.value && isSection(nextItem.value));

    const isFirst = nodes.getFirstKey() === item.value || isFirstInSection || isFirstAfterSection;
    const isLast = nodes.getLastKey() === item.value || isLastInSection || isLastBeforeSection;

    return { isFirst, isLast };
}
